<template>
    <div v-if="name" ref="receiptRef" class="view-receipt">
        <div class="view-receipt__documentPreview / flex">
            <div class="view-receipt__documentPreview__title / flex">
                <div class="view-receipt__documentPreview__title_icon"><VJoyIcon name="file-check" size="xxsmall" /></div>
                <div class="flex">
                    <div class="view-receipt__documentPreview__title_name / ellipsis">{{ name }}</div>
                    <div v-if="formattedUploadDate" class="view-receipt__documentPreview__title_date">
                        {{ $t('projectclient.projectview.details.documents.appendix.uploaded.date', [formattedUploadDate]) }}
                    </div>
                </div>
            </div>
            <div class="view-receipt_icons / flex">
                <VJoyIconButton v-if="canSeeReceiptOverview" icon="eye" variant="ghost" size="small" @click="displayEnlargedDocument" />
                <a v-if="downloadUrl" target="_blank" :href="downloadUrl">
                    <VJoyIconButton icon="download" variant="ghost" size="small" />
                </a>
                <VJoyIconButton v-if="deletable" icon="trash" variant="ghost" size="small" @click="emit('delete')" />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {computed, h, ref, render, useTemplateRef} from 'vue';
    import {useNuxtApp} from '#app';
    import {useFormat} from '#imports';
    import {useMediaQuery} from '@malt/utils';
    import {VJoyIcon, VJoyIconButton} from '@maltjoy/core-vue';
    import ReceiptOverview from './ReceiptOverview.vue';

    const props = defineProps<{
        name: string;
        uploadDate?: Date | string;
        downloadUrl?: string;
        deletable?: boolean;
        hidePreview?: boolean; // For now, preview doesn't work without a JoyDrawer context
    }>();

    const emit = defineEmits<{
        delete: [];
    }>();

    const nuxtApp = useNuxtApp();
    const {windowWidth} = useMediaQuery();
    const {formatDate} = useFormat();

    const receiptRef = useTemplateRef('receiptRef');

    const formattedUploadDate = computed(() => {
        if (props.uploadDate) {
            return formatDate(props.uploadDate);
        }
        return undefined;
    });

    const canSeeReceiptOverview = computed(() => {
        if (props.hidePreview) {
            return false;
        }

        const parentDrawer = receiptRef.value?.closest('.joy-drawer');
        const drawerWidth = parentDrawer?.querySelector('.joy-drawer--wrapper')?.clientWidth ?? 0;
        return windowWidth.value - drawerWidth > 300;
    });

    const renderedBody = ref<HTMLDivElement>();
    const isOverviewDisplayed = ref(false);

    function hideOverviewDisplayed() {
        const parentDrawer = receiptRef.value?.closest('.joy-drawer');
        if (parentDrawer && renderedBody.value) {
            parentDrawer.removeChild(renderedBody.value);
        }
        isOverviewDisplayed.value = false;
    }

    function displayEnlargedDocument() {
        if (isOverviewDisplayed.value) {
            hideOverviewDisplayed();
        } else {
            const parentDrawer = receiptRef.value?.closest('.joy-drawer');
            const drawerWidth = parentDrawer?.querySelector('.joy-drawer--wrapper')?.clientWidth;
            if (parentDrawer && drawerWidth && windowWidth.value - drawerWidth > 300) {
                if (renderedBody.value) {
                    parentDrawer.appendChild(renderedBody.value);
                } else {
                    renderedBody.value = document.createElement('div');
                    const overlayNode = h(ReceiptOverview, {
                        drawerWidth,
                        fileUrl: props.downloadUrl,
                        receiptName: props.name,
                        onCloseOverview: () => hideOverviewDisplayed(),
                    });
                    overlayNode.appContext = nuxtApp.vueApp._context;

                    render(overlayNode, renderedBody.value);
                    parentDrawer.appendChild(renderedBody.value);
                }
                isOverviewDisplayed.value = true;
            }
        }
    }
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .view-receipt {
        &__documentPreview {
            background: var(--joy-color-neutral-20);
            border-radius: var(--joy-core-radius-3);
            align-items: center;
            justify-content: space-between;
            padding: var(--joy-core-spacing-6);
            flex-flow: row nowrap;

            &__title {
                align-items: center;
                flex-flow: row nowrap;

                &_icon {
                    background: var(--joy-color-background-default);
                    border-radius: var(--joy-core-radius-2);
                    padding: var(--joy-core-spacing-3);
                    margin-right: var(--joy-core-spacing-6);
                }

                &_name {
                    @include text.getFontBySize(primary-350);

                    color: var(--joy-color-neutral-60);
                }

                &_date {
                    @include text.getFontBySize(primary-300);

                    color: var(--joy-color-neutral-40);
                    margin-bottom: 0;
                }
            }
        }

        &_icons {
            flex: 0 0 auto;
        }
    }
</style>
